import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  LucideArrowLeft,
  LucideEye,
  LucideEyeOff,
  LucideDownload,
  LucideSettings2,
  LucideLoader2,
} from "lucide-react";

/**
 * Top bar rendered inside every report form.
 *
 * Props:
 *   title            - report type name (e.g. "Ontario Progress Report Card")
 *   subtitle         - optional subtitle shown below the title (titleOnly mode only)
 *   titleOnly        - when true, renders only the centred title/subtitle with no back button or actions
 *   backUrl          - href for the Back button
 *   contextInfo      - { grade, schoolYear, oen, studentName } - shown as pill row below title
 *   showPreview      - controlled state for PDF panel visibility
 *   onTogglePreview  - () => void
 *   isPdfGenerating  - when true, download button shows spinner
 *   filledPDF        - Uint8Array|null; download disabled while null
 *   onDownload       - () => void
 *   defaultsGroupId  - when set, shows "Save Defaults" button
 *   onSaveDefaults   - () => void - called when Save Defaults is clicked
 */
export function ReportFormHeader({
  title,
  subtitle,
  titleOnly = false,
  backUrl,
  contextInfo,
  showPreview,
  onTogglePreview,
  isPdfGenerating,
  filledPDF,
  onDownload,
  defaultsGroupId,
  onSaveDefaults,
}) {
  const contextPills = contextInfo
    ? [
        contextInfo.grade && `Grade ${contextInfo.grade}`,
        contextInfo.schoolYear,
        contextInfo.oen && `OEN ${contextInfo.oen}`,
        contextInfo.studentName,
      ].filter(Boolean)
    : [];

  if (titleOnly) {
    return (
      <div className="flex items-center justify-center px-5 py-3 border-b border-border bg-card shrink-0">
        <div className="text-center">
          <h1 className="text-lg font-semibold text-foreground font-display">{title}</h1>
          {subtitle && (
            <p className="text-xs text-muted-foreground leading-none mt-0.5">{subtitle}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-card shrink-0">
      {/* Left - back */}
      <Link to={backUrl || "/"}>
        <Button variant="ghost" size="sm">
          <LucideArrowLeft size={16} className="mr-1.5" />
          Back
        </Button>
      </Link>

      {/* Centre - title + context */}
      <div className="text-center">
        <h1 className="text-lg font-semibold text-foreground font-display">{title}</h1>
        {contextPills.length > 0 && (
          <p className="text-xs text-muted-foreground leading-none mt-0.5">
            {contextPills.join(" · ")}
          </p>
        )}
      </div>

      {/* Right - actions */}
      <div className="flex items-center gap-2">
        {defaultsGroupId && (
          <Button
            size="sm"
            onClick={onSaveDefaults}
            className="bg-[hsl(var(--info))] hover:opacity-90 text-white border-[hsl(var(--info))]"
          >
            <LucideSettings2 size={14} className="mr-1.5" />
            Save Defaults
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onTogglePreview} className="gap-1.5">
          {showPreview ? <LucideEyeOff size={16} /> : <LucideEye size={16} />}
          <span className="text-xs">{showPreview ? "Hide PDF" : "Show PDF"}</span>
        </Button>
        <Button variant="outline" size="sm" onClick={onDownload} disabled={!filledPDF || isPdfGenerating}>
          {isPdfGenerating
            ? <LucideLoader2 size={14} className="animate-spin" />
            : <LucideDownload size={16} />
          }
        </Button>
      </div>
    </div>
  );
}
